import { useEffect, useRef, useState } from "react";
import { toast } from 'react-toastify';
import { AttachmentItem } from "../../../../../components/attachmentItem/AttachmentItem";
import { returnDate } from "../../../../../utils/formatDate";
import CommentService from "../../../../../services/resource/commentService";
import { DateTime } from "luxon";
import LocalStorageService from "../../../../../services/resource/localstorageService";

const initialComment = {
    comment: '',
    date: '',
    userEmail: null,
    readSender: false,
    readReceiver: true,
    file: false,
}

const messageLine = (message) => {
    return message.split("\n").map((line) => (<>{line}<br /></>))
}

export function ChatSection({ messages, user, service, ticket, attComments, status }) {
    const commentService = new CommentService();
    const [comment, setComment] = useState(initialComment);
    const textareaRef = useRef(null);
    const chatContainerRef = useRef(null); // este ref aponta para o scroll


    useEffect(() => {
        const textarea = textareaRef.current;

        const handleInput = () => {
            const textarea = textareaRef.current;
            textarea.style.height = 'auto';

            const lineCount = textarea.value.split('\n').length;

            // Altura máxima com base em 5 linhas (linha média ~24px com padding)
            const maxHeight = 24 * 5;

            if (textarea.scrollHeight > maxHeight) {
                textarea.style.height = `${maxHeight}px`;
                textarea.style.overflowY = 'auto';
            } else {
                textarea.style.height = `${textarea.scrollHeight}px`;
                textarea.style.overflowY = 'hidden';
            }
        };

        textarea.addEventListener('input', handleInput);

        return () => {
            textarea.removeEventListener('input', handleInput);
        };
    }, []);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            attComment();
            // Reset height after sending
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        }
        // Alt + Enter: insere nova linha (não previne o comportamento)
    };

    const attComment = async () => {
        if (comment.comment.trim() !== '') {
            try {
                await service.addComment(ticket, comment);
                setComment(initialComment);
                await attComments(); // carrega os novos comentários
            } catch {
                toast.error("Erro ao proseguir com solicitação. Seu comentário não foi salvo!");
            }
        }
    };

    const deleteComment = (comment) => {
        const data = comment;
        commentService.del(data.id, data.userEmail).then(() => {
            attComments()
        }).catch((error) => {
            toast.error(error.response.data.message);
        })
    }

    return (
        <div className="bg-white rounded-lg shadow">
            <div className="border-b border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <i className="fas fa-comments mr-2 text-indigo-600"></i> Conversa sobre o chamado
                </h3>
            </div>

            <div ref={chatContainerRef} className="chat-container p-4 overflow-y-auto">
                <div className="space-y-4">
                    {messages.map((commentObj, index) => (
                        <>
                            {commentObj.userName ?
                                <div key={index}>
                                    {user === commentObj.userEmail &&
                                        <MessageFromUser
                                            message={commentObj.comment}
                                            messageDate={returnDate(commentObj.date)}
                                            filename={commentObj.filename}
                                            usuario={commentObj.userName}
                                            service={service}
                                            deleteComment={() => { deleteComment(commentObj) }}
                                        />}
                                    {user !== commentObj.userEmail &&
                                        <MessageSuport
                                            message={commentObj.comment}
                                            messageDate={returnDate(commentObj.date)}
                                            filename={commentObj.filename}
                                            usuario={commentObj.userName}
                                            service={service}
                                        />}
                                </div>
                                :
                                <div className='d-flex justify-content-center mb-3'>
                                    {commentObj.comment}
                                </div>
                            }
                        </>
                    ))}
                </div>
            </div>

            <div className="border-t border-gray-200 py-1 px-3 py-3">
                <form onSubmit={(e) => { e.preventDefault(); attComment(); }} className="flex space-x-2">
                    <textarea
                        ref={textareaRef}
                        id="messageInput"
                        placeholder={status === "ABERTO" || status === "EXECUCAO" ? "Digite sua mensagem..." : "Chamado não pode receber mais mensagens"}
                        className="w-full px-4 py-2 border-1 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none overflow-hidden"
                        name="comment"
                        rows={1}
                        value={comment.comment}
                        disabled={status === "CANCELADO" || status === "RESOLVIDO"}
                        onChange={(e) => setComment({
                            ...comment,
                            comment: e.target.value,
                            date: DateTime.now().toFormat("yyyy-MM-dd HH:mm:ss"),
                            userEmail: LocalStorageService.getItem("@YORK:User").email,
                            readSender: false,
                            readReceiver: true
                        })}
                        onKeyDown={handleKeyDown}
                    />
                    <button
                        type="submit"
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        style={{ maxHeight: '44px' }}
                        disabled={comment.comment === ""}
                    >
                        <i className="fas fa-paper-plane"></i>
                    </button>
                </form>
            </div>
        </div>
    )
};

function MessageSuport({ message, usuario, messageDate, filename, service }) {
    return (
        <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-headset text-indigo-600"></i>
            </div>
            <div>
                <div className="message-support p-2 max-w-xs md:max-w-md">
                    {filename !== null ?
                        <AttachmentItem filename={filename} service={service} />
                        :
                        <p>{messageLine(message)}</p>
                    }
                </div>
                <p className="text-xs text-gray-500 mt-1">{usuario} - {messageDate}</p>
            </div>
        </div>)
}

function MessageFromUser({ message, service, messageDate, filename, deleteComment }) {
    return (
        <div className="flex items-start space-x-3 justify-end">
            <div>
                <div className="message-user p-3 max-w-xs md:max-w-md">
                    {filename !== null ?
                        <AttachmentItem filename={filename} service={service} />
                        :
                        <p>{messageLine(message)}</p>
                    }
                </div>
                <p className="text-xs text-gray-500 mt-1 text-right">
                    Você - {messageDate}
                    <i className="fas fa-times text-red-600 ml-2 cursor-pointer" onClick={deleteComment}></i>
                </p>
            </div>
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-user text-blue-600"></i>
            </div>
        </div>
    )
}